import { saveAs } from 'file-saver';
import Excel from 'exceljs';
import { projectReportColumnsXLS } from './xlsColumns';
import { sortByDefault } from '../utils/filtering';
import { ProjectReport } from '../api/api-types';

export const exportProjectReport = async (reportProject: ProjectReport) => {
  const workbook = new Excel.Workbook();
  try {
    const fileName = `${reportProject.projectName?.name} project report ${reportProject.periodStartDate}-${reportProject.periodEndDate}`;

    const worksheet = workbook.addWorksheet('Project report');

    worksheet.columns = projectReportColumnsXLS;

    worksheet.getRow(1).eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'A6CCF7' },
      };
      cell.font = { bold: true };
    });

    const addSubtotalRow = (employeeName: string, subTotalValue: string) => {
      const createRow = worksheet.addRow({});
      createRow.getCell('A').value = {
        richText: [{ font: { bold: true }, text: 'Subtotal: ' }],
      };
      createRow.getCell('B').value = {
        richText: [{ font: { bold: true }, text: `${employeeName}` }],
      };
      createRow.getCell('F').value = {
        richText: [{ font: { bold: true }, text: `${subTotalValue}` }],
      };
      createRow.getCell('G').value = {
        richText: [{ font: { bold: true }, text: ' ' }],
      };
      createRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E0E0E0' },
        };
      });
      return createRow;
    };

    const createDivider = () => {
      const divider = worksheet.addRow({});
      divider.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFF' },
      };
      return divider;
    };

    const addTotalRow = (totalValue: string, rowNumber: number) => {
      const getLastRow = worksheet.getRow(rowNumber + 1);
      getLastRow.getCell('A').value = {
        richText: [{ font: { bold: true }, text: 'Total: ' }],
      };
      getLastRow.getCell('F').value = {
        richText: [{ font: { bold: true }, text: `${totalValue}` }],
      };
      getLastRow.getCell('G').value = {
        richText: [{ font: { bold: true }, text: ' ' }],
      };
      getLastRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E0E0E0' },
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
      getLastRow.commit();
    };

    reportProject?.employeeTaskTimeLogsList?.map((employee) =>
      [
        ...(employee.timeLogs ?? []).map((el) =>
          worksheet.addRow({
            date: el.date,
            user: employee.employeeFullName?.name,
            client: reportProject.customerFullName?.name,
            project: reportProject.projectName?.name,
            task: employee.taskName,
            duration: el.duration,
            note: el.note,
          }),
        ),
        addSubtotalRow(
          employee.employeeFullName?.name ?? '',
          employee.subtotalDuration ?? '',
        ),
        createDivider(),
        //TODO: probably not possible to type, as library does not provide normal ways to do so
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
      ].sort((a, b) => sortByDefault(a, b, 'Date')),
    );

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      if (column.eachCell) {
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 24;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        });
      }
      column.width =
        column.header !== 'Note' && maxLength > 24
          ? 24
          : column.header === 'Note'
            ? 54
            : maxLength;
      column.alignment =
        column.header !== 'Note'
          ? { vertical: 'middle', horizontal: 'center', wrapText: true }
          : { vertical: 'middle', horizontal: 'left', wrapText: true };
    });

    const { lastRow } = worksheet;

    if (lastRow?.number)
      addTotalRow(reportProject?.totalHours ?? '', lastRow.number);

    const buf = await workbook.xlsx.writeBuffer();

    saveAs(new Blob([buf]), `${fileName}.xlsx`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return error.message;
  } finally {
    workbook.removeWorksheet('Project report');
  }
};
